import { useMemo } from "react";
import { useQuery } from "@apollo/client/react";
import { GetMemberDocument, type MemberProfileRow } from "#app/lib/data";
import type { Omittable } from "@salestrak/ui";

// The query's own row type travels unchanged — no re-shaping, and no coercion
// of the nullable `email` (R8).
export type MemberDetailUser = MemberProfileRow;

type UseMemberDetailOptions = Omittable<{
  /** Additional skip condition (query already skips when userId is falsy) */
  skip?: boolean;
  /** Hover previews pass "cache-first" so pointer travel doesn't spam network. */
  fetchPolicy?: "cache-and-network" | "cache-first";
}>;

export function useMemberDetail(userId: string | undefined, options?: UseMemberDetailOptions) {
  const variables = useMemo(() => ({ id: userId! }), [userId]);

  const { data, loading, error } = useQuery(GetMemberDocument, {
    variables,
    fetchPolicy: options?.fetchPolicy ?? "cache-and-network",
    skip: !userId || (options?.skip ?? false),
  });

  // `user` and `member` were always the same row — one re-shaped, one not.
  const member = data?.user ?? null;

  return { user: member, member, loading, error };
}
